/**
 * Unique string to identify the type network service.
 * Pulling from `axiosConfig.agilemdType`
 * @typedef {string} Key
 */

/**
 * @typedef {Object} RequestStartOptions
 * @property {Key} RequestStartOptions.key - The key of the request
 * @property {number} RequestStartOptions.startTime - The time the request started
 */
/**
 * @typedef {Object} RequestEndOptions
 * @property {Key} RequestEndOptions.key - The key of the request
 * @property {number} RequestEndOptions.startTime - The time the request started
 * @property {Error} [RequestEndOptions.error] - The error that occurred if any
 */

/**
 * @callback OnStartRequest
 * @param {RequestStartOptions} options
 * @returns {void}
 */
/**
 * @callback OnFinishRequest
 * @param {RequestEndOptions} options
 * @returns {void}
 */

/**
 * @typedef {Object} NetworkOptions
 * @property {OnStartRequest} NetworkOptions.onStartRequest
 * @property {OnFinishRequest} NetworkOptions.onFinishRequest
 */

/**
 * @param {import('axios').AxiosInstance} axios
 */
export default function networkFactory(axios) {
  /**
   * Calls the `onStartRequest` callback when the request begins.
   * Calls the `onFinishRequest` callback when the request finishes.
   * @example
   * interceptNetwork({
   *  onStartRequest: ctx => store.dispatch(startNetworkRequest(ctx)),
   *  onFinishRequest: ctx => store.dispatch(finishNetworkRequest(ctx))
   * })
   * @function syncNetworkState
   * @param {NetworkOptions} options
   * @returns {void}
   */
  return function interceptNetwork({ onStartRequest, onFinishRequest }) {
    axios.interceptors.request.use(config => {
      if (!config.agilemdType) {
        return config;
      }

      const startTime = Date.now();
      onStartRequest({ key: config.agilemdType, startTime });

      return {
        ...config,
        startTime
      };
    });

    axios.interceptors.response.use(
      res => {
        const { config } = res;

        if (config.agilemdType) {
          onFinishRequest({
            key: config.agilemdType,
            startTime: config.startTime
          });
        }

        return res;
      },
      err => {
        const { config } = err;

        if (config?.agilemdType) {
          let errorMessage;

          if (!err.response) {
            errorMessage = 'Could not complete request';
          } else {
            const { status: statusCode, statusText } = err.response;
            errorMessage = `${statusText} [${statusCode}]`;
          }

          onFinishRequest({
            key: config.agilemdType,
            startTime: config.startTime,
            error: errorMessage
          });
        }

        return Promise.reject(err);
      }
    );
  };
}
